/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState } from "react";
import { Button, Chip, List, ListItem, ListItemText, ListSubheader, TextField } from "@material-ui/core";
import PriorityHighIcon from "@material-ui/icons/PriorityHigh";

/**
 * This component displays all the users in a list. It will display one record per user with their email and name.
 * These users are selectable so that they can be passed back to the page for further inspection.
 * @param {Object} usersObject The PACE user records object.
 * @param {(newSelectedUser) => void} selectUserHandler A callback method for passing the new selected user back to the page.
 * @param {Object} selectedUser The selected user, passed in so we can highlight the user in the list.
 */
export const UserList = ({
	usersObject,
	newActionButtons,
	selectUserHandler,
	selectedUser,
	roleTypes,
	onSyncClick,
}) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [displayUserList, setDisplayUserList] = useState([]);

	React.useEffect(() => {
		setDisplayUserList(usersObject ? Object.values(usersObject).sort((a, b) => (a.userId > b.userId ? 1 : -1)) : []);
	}, [usersObject]);

	const searchMatches =
		displayUserList &&
		displayUserList.filter((user) => {
			const searchTermLowerCase = searchTerm.toLowerCase();
			return (
				user.userId.toLowerCase().includes(searchTermLowerCase) ||
				(user.username && user.username.toLowerCase().includes(searchTermLowerCase)) ||
				(user.roles &&
					user.roles.find((role) => roleTypes[role].roleName.toLowerCase().includes(searchTermLowerCase)) !== undefined)
			);
		});
	return (
		<div>
			<List
				component="nav"
				subheader={
					<ListSubheader disableSticky component="div">
						{newActionButtons}
						<div className="my-2">
							<TextField
								style={{ width: "100%" }}
								size="small"
								label="Search by email or role"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
							/>
						</div>
						<div className="d-flex flex-row justify-content-between align-items-center flex-wrap">
							<span>Users</span>
							<Button variant="outlined" className="mb-2 mb-md-0" size="small" onClick={onSyncClick}>
								Sync New Users
							</Button>
						</div>
					</ListSubheader>
				}
			>
				{searchMatches?.length ? (
					searchMatches.map((user) => {
						let userError = false;
						user.roles?.forEach((roleId) => {
							if (!roleTypes[roleId]) {
								userError = true;
							}
						});
						return (
							<ListItem
								button
								selected={selectedUser && user.userId === selectedUser.userId}
								onClick={() => selectUserHandler(user)}
								key={user.userId}
								style={{ display: "flex", opacity: 1 }}
							>
								<ListItemText
									style={{ overflowWrap: "break-word" }}
									primary={user.edited ? <h6>{user.email}*</h6> : user.email}
									secondary={user.roles?.map((roleId) =>
										roleTypes[roleId] ? (
											<Chip
												key={roleId}
												label={roleTypes[roleId].roleName}
												variant="outlined"
												size="small"
												style={{ marginLeft: "5px" }}
												component={"span"}
											/>
										) : (
											<Chip
												key={roleId}
												label={"Unknown"}
												color="secondary"
												variant="outlined"
												size="small"
												component={"span"}
												style={{ marginLeft: "5px" }}
											/>
										),
									)}
								/>
								{userError && <PriorityHighIcon style={{ color: "red" }} />}
								{user.newUser && <h6>(New)</h6>}
							</ListItem>
						);
					})
				) : (
					<ListItem>
						<span className="text-muted font-italic">{"No users found"}</span>
					</ListItem>
				)}
			</List>
		</div>
	);
};
